import React, { useEffect, useMemo, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { change, Field, formValueSelector } from 'redux-form'
import { Input, InputWithIcon } from '../../../../../../../../../itrust_common/components/form'
import SpecteraFormButton from '../../../spectera/claimForm/SpecteraFormButton'
import { formatVersantCodeLabel, getStep2Fields, normalizeVersantCodes } from '../versantClaimHelper'
import VersantHcpcsDiagnosisModal from './VersantHcpcsDiagnosisModal'

const selector = formValueSelector('VersantClaimForm')
const req = (label) => <>{label} <span className="text-danger">*</span></>
const PREFIX = 'versant.final'
const ROWS_FIELD = `${PREFIX}.hcpcs_rows`

const buildRows = (procedures = [], existingRows = []) => (
  normalizeVersantCodes(procedures).map((procedure, index) => {
    const existing = existingRows?.[index]?.code === procedure.code
      ? existingRows[index]
      : {}
    return {
      code: procedure.code,
      description: procedure.description || '',
      modifier: existing.modifier || '',
      uc_charge: existing.uc_charge ?? '0',
      units: existing.units || '1',
      authorization: existing.authorization || '',
      diagnosis_codes: normalizeVersantCodes(existing.diagnosis_codes).map((item) => ({
        ...item,
        primary: !!item.primary,
      })),
    }
  })
)

function DiagnosisCell({ codes = [], onOpen }) {
  const selected = normalizeVersantCodes(codes)

  return (
    <div>
      <button
        type="button"
        className="btn btn-outline-primary rounded-circle d-inline-flex align-items-center justify-content-center p-0"
        style={{ width: 22, height: 22, minWidth: 22, lineHeight: 1 }}
        onClick={onOpen}
        aria-label="Select diagnosis codes"
      >
        <span className="font-weight-bold" style={{ fontSize: 14, lineHeight: 1 }}>+</span>
      </button>
      {selected.length > 0 && (
        <div className="mt-2">
          {selected.map((code) => (
            <div key={code.code} className="small text-break mb-1">
              {formatVersantCodeLabel(code)}
              {code.primary ? <span className="ml-2 font-weight-bold text-primary">Primary</span> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function VersantFinalStep({
  diagnosisCodes,
  procedureCodes,
  hcpcsRows,
  backStep,
}) {
  const dispatch = useDispatch()
  const procedures = useMemo(() => normalizeVersantCodes(procedureCodes), [procedureCodes])
  const rows = Array.isArray(hcpcsRows) ? hcpcsRows : []
  const [modalRowIndex, setModalRowIndex] = useState(null)

  useEffect(() => {
    const nextRows = buildRows(procedures, rows)
    const sameLength = nextRows.length === rows.length
    const sameCodes = sameLength && nextRows.every((row, index) => row.code === rows[index]?.code)
    if (!sameCodes) {
      dispatch(change('VersantClaimForm', ROWS_FIELD, nextRows))
    }
  }, [dispatch, procedures, rows])

  const totalUcCharge = rows.reduce((sum, row) => sum + (Number(row?.uc_charge) || 0), 0)
  const activeRow = modalRowIndex == null ? null : rows[modalRowIndex]

  const handleDiagnosisSelect = (selectedCodes) => {
    if (modalRowIndex == null) return
    const nextRows = rows.map((row, index) => (
      index === modalRowIndex
        ? { ...row, diagnosis_codes: selectedCodes }
        : row
    ))
    dispatch(change('VersantClaimForm', ROWS_FIELD, nextRows))
    setModalRowIndex(null)
  }

  return (
    <div className="bg-white p-6 border rounded">
      <p className="mb-2">Please select the HCPCS Code based on the Prescription range.</p>
      <p className="text-muted mb-4">*Note: When applicable, enter the total U&amp;C charge for the line. Do not multiply with Days or Units.</p>

      <div className="table-responsive">
        <table className="table table-bordered mb-0">
          <thead>
            <tr>
              <th>HCPCS Codes</th>
              <th>Description</th>
              <th style={{ width: '90px' }}>Modifier/Internal Code</th>
              <th style={{ width: '100px' }}>{req('U&C charges')}</th>
              <th style={{ width: '120px' }}>{req('Day(s) or Unit(s)')}</th>
              <th>Diagnosis Codes</th>
              <th style={{ width: '90px' }}>{req('Authorization')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan="7" className="text-muted text-center">No HCPCS codes selected.</td>
              </tr>
            )}
            {rows.map((row, index) => (
              <tr key={row.code || index}>
                <td>{row.code || '-'}</td>
                <td>{row.description || '-'}</td>
                <td style={{ width: '90px' }}>
                  <Field
                    name={`${ROWS_FIELD}[${index}].modifier`}
                    component={Input}
                    className="form-control"
                    wrapperClasses="form-group mb-0"
                    disabled
                  />
                </td>
                <td style={{ width: '100px' }}>
                  <Field
                    name={`${ROWS_FIELD}[${index}].uc_charge`}
                    component={InputWithIcon}
                    title="$"
                    className="form-control"
                    wrapperClasses="form-group mb-0"
                  />
                </td>
                <td style={{ width: '110px' }}>
                  <Field
                    name={`${ROWS_FIELD}[${index}].units`}
                    component={Input}
                    className="form-control"
                    wrapperClasses="form-group mb-0"
                  />
                </td>
                <td>
                  <DiagnosisCell
                    codes={row.diagnosis_codes}
                    onOpen={() => setModalRowIndex(index)}
                  />
                </td>
                <td style={{ width: '90px' }}>
                  <Field
                    name={`${ROWS_FIELD}[${index}].authorization`}
                    component={Input}
                    className="form-control"
                    wrapperClasses="form-group mb-0"
                    disabled
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-5">
        <strong>Total U&amp;C Charges: ${Number(totalUcCharge || 0).toFixed(2)}</strong>
        <span className="text-muted">* Indicates Required</span>
      </div>

      <SpecteraFormButton backStep={backStep} buttonClasses="py-2" />

      <VersantHcpcsDiagnosisModal
        open={modalRowIndex != null}
        hcpcsCode={activeRow?.code}
        hcpcsDescription={activeRow?.description}
        availableDiagnosisCodes={diagnosisCodes}
        selectedDiagnosisCodes={activeRow?.diagnosis_codes}
        onCancel={() => setModalRowIndex(null)}
        onSelect={handleDiagnosisSelect}
      />
    </div>
  )
}

export default connect((state) => {
  const eligibilityData = selector(state, 'eligibility.data') || []
  const fields = getStep2Fields(eligibilityData)
  const hasSpectacleStep = fields.includes('spectacle_lens') && fields.some((field) => field !== 'spectacle_lens')

  return {
    diagnosisCodes: selector(state, 'services.diagnosisCodes'),
    procedureCodes: selector(state, 'services.procedureCodes'),
    hcpcsRows: selector(state, ROWS_FIELD) || [],
    backStep: hasSpectacleStep ? 'spectacle' : 'second',
  }
})(VersantFinalStep)
